import { Cause, Exit, Option, Schema } from "effect";
import { TrimmedNonEmptyString } from "./baseSchemas";

export const PROJECT_CONFIG_RELATIVE_PATH = ".taskin/config.json";

const withDefaults =
  <
    S extends Schema.Top & Schema.WithoutConstructorDefault,
    D extends S["~type.make.in"] & S["Encoded"],
  >(
    fallback: () => D,
  ) =>
  (schema: S) =>
    schema.pipe(
      Schema.withConstructorDefault(() => Option.some(fallback())),
      Schema.withDecodingDefault(() => fallback()),
    );

export const ProjectThreadConfig = Schema.Struct({
  planModeByDefault: Schema.Boolean.pipe(withDefaults(() => false)),
});
export type ProjectThreadConfig = typeof ProjectThreadConfig.Type;

export const ProjectConfig = Schema.Struct({
  version: Schema.Literal(1).pipe(withDefaults(() => 1 as const)),
  thread: ProjectThreadConfig.pipe(withDefaults(() => ({ planModeByDefault: false }))),
});
export type ProjectConfig = typeof ProjectConfig.Type;

export const DEFAULT_PROJECT_CONFIG: ProjectConfig = ProjectConfig.makeUnsafe({});

export const DEFAULT_PROJECT_CONFIG_JSON = `${JSON.stringify(DEFAULT_PROJECT_CONFIG, null, 2)}\n`;

const decodeProjectConfig = Schema.decodeUnknownExit(ProjectConfig);

export type ParseProjectConfigResult =
  | { readonly ok: true; readonly config: ProjectConfig }
  | { readonly ok: false; readonly error: string };

export function parseProjectConfig(raw: string): ParseProjectConfigResult {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch (cause) {
    return {
      ok: false,
      error: cause instanceof Error ? cause.message : "Invalid JSON in project config.",
    };
  }
  const exit = decodeProjectConfig(parsed);
  if (Exit.isFailure(exit)) {
    return { ok: false, error: Cause.pretty(exit.cause) };
  }
  return { ok: true, config: exit.value };
}

export const ProjectReadConfigInput = Schema.Struct({
  cwd: TrimmedNonEmptyString,
});
export type ProjectReadConfigInput = typeof ProjectReadConfigInput.Type;

export const ProjectReadConfigResult = Schema.Union([
  Schema.Struct({
    exists: Schema.Literal(false),
  }),
  Schema.Struct({
    exists: Schema.Literal(true),
    valid: Schema.Literal(false),
    error: Schema.String,
  }),
  Schema.Struct({
    exists: Schema.Literal(true),
    valid: Schema.Literal(true),
    config: ProjectConfig,
  }),
]);
export type ProjectReadConfigResult = typeof ProjectReadConfigResult.Type;
